/*
 * Orvian Meet — utilidades sin interfaz: API, nombres, colores, horas y preferencias.
 * Contrato de la API en claude/meet.md. Todo pasa por el `api` del portal (axios con el token
 * del correo, que en meet.orvian.mx trae sesionCorreo.js).
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { hora12 } from '../lib/format';
import { useStore } from '../store/useStore';
import { useQuery } from '@tanstack/react-query';

export const CODIGO_RE = /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/;

/** Lo que escribe la persona («abc-defg-hij», «abcdefghij», o el enlace entero) → código o null. */
export function extraerCodigo(texto) {
  const t = String(texto || '').trim().toLowerCase();
  if (!t) return null;
  const enUrl = /([a-z]{3}-[a-z]{4}-[a-z]{3})(?:[/?#]|$)/.exec(t);
  if (enUrl) return enUrl[1];
  const letras = t.replace(/[^a-z]/g, '');
  if (letras.length === 10 && /^[a-z\s-]+$/.test(t)) return `${letras.slice(0, 3)}-${letras.slice(3, 7)}-${letras.slice(7)}`;
  return null;
}

/* ---------------- API ---------------- */
export const meetApi = {
  crear: (datos = {}) => api.post('/meet/salas', datos).then((r) => r.data),
  mias: () => api.get('/meet/salas').then((r) => r.data?.salas || []),
  ver: (codigo) => api.get(`/meet/salas/${codigo}`).then((r) => r.data),
  ficha: (codigo, nombre) => api.post(`/meet/salas/${codigo}/ficha`, nombre ? { nombre } : {}).then((r) => r.data),
  moderar: (codigo, identidad, accion, trackSid) =>
    api.post(`/meet/salas/${codigo}/moderar`, { identidad, accion, track_sid: trackSid || undefined }).then((r) => r.data),
};

/** Texto de error de una petición, en castellano y para personas. */
export function textoError(err, porDefecto = 'Algo salió mal. Inténtalo de nuevo.') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string' && d) return d;
  if (err?.response?.status === 429) return 'Demasiados intentos seguidos. Espera un momento.';
  if (!err?.response && err?.message) return 'No hay conexión con el servidor.';
  return porDefecto;
}

/**
 * El backend trata un token caducado como «sin sesión» en las rutas de sesión opcional (ver/ficha), así que
 * antes de usarlas se confirma la sesión con una ruta que SÍ la exige: si el token caducó, el interceptor
 * de api.js lo renueva. Si no se puede, se sigue como invitado (y el store lo refleja).
 */
export async function asegurarSesion() {
  if (!localStorage.getItem('zuno_access')) {
    if (useStore.getState().user) useStore.setState({ user: null });
    return false;
  }
  try {
    const r = await api.get('/auth/me');
    if (r?.data) useStore.setState({ user: { ...(useStore.getState().user || {}), ...r.data } });
    return true;
  } catch (e) {
    if (e?.response?.status === 401) {
      useStore.setState({ user: null });
      return false;
    }
    return !!useStore.getState().user; // sin red: se confía en lo que había
  }
}

/* ---------------- Personas ---------------- */
export function iniciales(nombre) {
  const partes = String(nombre || '').replace(/[<>@].*$/, '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  const a = partes[0][0] || '';
  const b = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (a + b).toUpperCase();
}

const TONOS = ['#2563eb', '#0f766e', '#9333ea', '#c2410c', '#be123c', '#4d7c0f', '#0369a1', '#a16207', '#7c3aed', '#047857'];
export function colorDe(clave) {
  let h = 0;
  const s = String(clave || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return TONOS[h % TONOS.length];
}

export function metaDe(participante) {
  try {
    return JSON.parse(participante?.metadata || '{}') || {};
  } catch {
    return {};
  }
}

export const nombreDe = (p) => p?.name || p?.identity || 'Participante';

/* ---------------- Horas ---------------- */
// 12 h («6:32 p. m.») como todo el portal (decisión de Gabriel, 2026-10-06); el navegador daba «06:32 p.m.»
export const fmtHora = (d) => hora12(d);

export function fmtFechaLarga(d) {
  return new Date(d).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function fmtCuando(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(hoy.getTime() - 86400000);
  const mismoDia = (a, b) => a.toDateString() === b.toDateString();
  if (mismoDia(d, hoy)) return `hoy, ${fmtHora(d)}`;
  if (mismoDia(d, ayer)) return `ayer, ${fmtHora(d)}`;
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: d.getFullYear() === hoy.getFullYear() ? undefined : 'numeric' });
}

/** Reloj que se actualiza cada `cada` ms (la hora de la cabecera y de la barra de la reunión). */
export function useReloj(cada = 15000) {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), cada);
    return () => clearInterval(t);
  }, [cada]);
  return ahora;
}

/* ---------------- Preferencias (solo en meet.orvian.mx) ---------------- */
const PREFS = 'zuno_meet_prefs';
export function leerPrefs() {
  try {
    return { mic: true, cam: true, ...(JSON.parse(localStorage.getItem(PREFS) || '{}') || {}) };
  } catch {
    return { mic: true, cam: true };
  }
}
export function guardarPrefs(cambios) {
  try {
    localStorage.setItem(PREFS, JSON.stringify({ ...leerPrefs(), ...cambios }));
  } catch {
    /* sin almacenamiento */
  }
}

export async function copiar(texto) {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

/** Error de getUserMedia → explicación con qué hacer. */
export function textoErrorMedios(err, que = 'la cámara y el micrófono') {
  const n = err?.name || '';
  if (n === 'NotAllowedError' || n === 'SecurityError' || n === 'PermissionDeniedError') {
    return {
      titulo: `No tienes permiso para usar ${que}`,
      texto: 'Pulsa el candado o el icono de cámara junto a la dirección (meet.orvian.mx), permite la cámara y el micrófono y vuelve a cargar la página.',
      permiso: true,
    };
  }
  if (n === 'NotFoundError' || n === 'DevicesNotFoundError' || n === 'OverconstrainedError') {
    return { titulo: `No se encontró ${que}`, texto: 'Conecta un dispositivo o elige otro en Ajustes.' };
  }
  if (n === 'NotReadableError' || n === 'TrackStartError' || n === 'AbortError') {
    return { titulo: `${que[0].toUpperCase()}${que.slice(1)} está en uso`, texto: 'Otra aplicación o pestaña la está usando. Ciérrala e inténtalo de nuevo.' };
  }
  return { titulo: `No se pudo usar ${que}`, texto: err?.message || 'Inténtalo de nuevo.' };
}

/** Configuración de Meet para esta sesión: `llamadas` (las directas tipo WhatsApp, apagadas por defecto en el servidor). */
export function useConfigMeet() {
  const user = useStore((s) => s.user);
  const q = useQuery({
    queryKey: ['meet-config', user?.id || null],
    queryFn: () => api.get('/meet/config', { skipAuthRedirect: true }).then((r) => r.data).catch(() => ({ llamadas: false })),
    staleTime: 300000,
  });
  return q.data || { llamadas: false };
}

/* ---------------- Pantalla compartida desde la app del teléfono ---------------- */
/** El «acompañante» con el que la app Orvian Mail (Android) comparte la pantalla: otro participante de la misma
 *  persona que solo publica la pantalla (`metadata.pantalla`, `metadata.de` = identidad del principal). */
export const esAcompanante = (p) => !!metaDe(p).pantalla;
export const acompananteDe = (p) => metaDe(p).de || '';

/** Dentro de la app Orvian Mail (vista web con el canal `OrvianMeet`) y en Android: la app sabe capturar la pantalla. */
export const appComparte = () => {
  try {
    return !!window.OrvianMeet && sessionStorage.getItem('zuno_meet_app') === '1' && /Android/i.test(navigator.userAgent);
  } catch { return false; }
};

/** Pide a la app que empiece o deje de compartir la pantalla de este teléfono. */
export function pedirPantallaALaApp(compartir, codigo, de) {
  try {
    window.OrvianMeet.postMessage(JSON.stringify(compartir ? { evento: 'compartir_pantalla', codigo, de } : { evento: 'dejar_pantalla' }));
  } catch { /* fuera de la app no hace nada */ }
}

/** Dentro de la app (cualquier plataforma con el canal `OrvianMeet`). */
export const enAppOrvian = () => {
  try { return !!window.OrvianMeet && sessionStorage.getItem('zuno_meet_app') === '1'; } catch { return false; }
};

/** Audio de la llamada en la app: auricular/altavoz, sensor de proximidad y pantalla encendida (AudioLlamada.kt). */
export function audioEnLaApp(enLlamada, altavoz = true) {
  try { window.OrvianMeet.postMessage(JSON.stringify({ evento: 'audio', enLlamada, altavoz })); } catch { /* fuera de la app */ }
}
